import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Play, Pause, Square, Timer } from "lucide-react";

export const fmtDur = (s) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
};

function TimerRow({ t, act }) {
  const [now, setNow] = useState(t.seconds);
  useEffect(() => { setNow(t.seconds); if (t.status !== "running") { return undefined; } const id = setInterval(() => setNow((x) => x + 1), 1000); return () => clearInterval(id); }, [t.seconds, t.status]);
  const big = "h-12 min-w-[3rem] px-3 rounded-xl inline-flex items-center justify-center gap-1 text-sm font-bold uppercase transition-colors";
  return (
    <div data-testid={`timer-${t.id}`} className="flex items-center gap-2 rounded-xl border border-zinc-800 bg-zinc-900/40 p-2">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-zinc-100 truncate">{t.mechanic}</div>
        <div className={`font-mono text-lg tabular-nums ${t.status === "running" ? "text-green-300" : t.status === "done" ? "text-zinc-400" : "text-amber-300"}`}>{fmtDur(now)} <span className="text-[11px] font-sans text-zinc-500">{t.status === "running" ? "rodando" : t.status === "paused" ? "pausado" : "finalizado"}</span></div>
      </div>
      {t.status === "running" && <button type="button" data-testid={`timer-${t.id}-pause`} onClick={() => act(t.id, "pause")} className={`${big} bg-amber-500 text-black hover:bg-amber-400`}><Pause className="h-5 w-5" /></button>}
      {t.status === "paused" && <button type="button" data-testid={`timer-${t.id}-resume`} onClick={() => act(t.id, "resume")} className={`${big} bg-green-brand text-white hover:brightness-110`}><Play className="h-5 w-5" /></button>}
      {t.status !== "done" && <button type="button" data-testid={`timer-${t.id}-finish`} onClick={() => act(t.id, "finish")} className={`${big} bg-zinc-800 text-zinc-100 hover:bg-zinc-700`}><Square className="h-4 w-4" /> Fim</button>}
    </div>
  );
}

export function OrderTimers({ order, readOnly }) {
  const [timers, setTimers] = useState([]);
  const [mechs, setMechs] = useState([]);
  const [sel, setSel] = useState({});
  const items = order?.budget?.items || [];
  useEffect(() => {
    api.get(`/orders/${order.id}/timers`).then(({ data }) => setTimers(data)).catch(() => setTimers([]));
    api.get("/erp/mechanics").then(({ data }) => setMechs((data || []).map((m) => (typeof m === "string" ? m : m.name)))).catch(() => setMechs([]));
  }, [order.id]);
  if (!items.length) { return null; }
  const start = async (i) => {
    const mechanic = sel[i] || order.mechanic_name || mechs[0];
    if (!mechanic) { toast.error("Cadastre um mecânico primeiro"); return; }
    try { const { data } = await api.post(`/orders/${order.id}/timers`, { item_index: i, mechanic }); setTimers(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  const act = async (tid, a) => {
    try { const { data } = await api.post(`/orders/${order.id}/timers/${tid}/${a}`); setTimers(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  const total = timers.reduce((s, t) => s + t.seconds, 0);
  return (
    <section data-testid="order-timers" className="rounded-2xl border border-card2-border bg-card2 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold uppercase text-zinc-100 flex items-center gap-2"><Timer className="h-5 w-5 text-green-300" /> Tempo dos serviços</h2>
        <span data-testid="timers-total" className="text-sm text-zinc-400">Total real: <b className="text-zinc-100 font-mono">{fmtDur(total)}</b></span>
      </div>
      {items.map((it, i) => {
        const ts = timers.filter((t) => t.item_index === i);
        return (
          <div key={i} data-testid={`timer-item-${i}`} className="space-y-2 border-t border-zinc-800 pt-3 first:border-0 first:pt-0">
            <div className="text-sm text-zinc-200 font-semibold">{it.description}</div>
            {ts.map((t) => <TimerRow key={t.id} t={t} act={act} />)}
            {!readOnly && (
              <div className="flex gap-2">
                <select data-testid={`timer-item-${i}-mech`} value={sel[i] || order.mechanic_name || ""} onChange={(e) => setSel((p) => ({ ...p, [i]: e.target.value }))} className="flex-1 min-w-0 h-12 rounded-xl bg-zinc-900 border border-zinc-800 px-3 text-sm text-zinc-100">
                  <option value="">Mecânico…</option>
                  {mechs.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <button type="button" data-testid={`timer-item-${i}-start`} onClick={() => start(i)} className="h-12 px-4 rounded-xl bg-green-brand text-white font-bold uppercase text-sm inline-flex items-center gap-1 hover:brightness-110"><Play className="h-5 w-5" /> Iniciar</button>
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}
